import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import MediaComponent from '../Media/media-component.js';
import { isPastEvent } from '../Events/event-date-utils.js';

// Per-section heading + "view all" target, bilingual. Kept local so the mega
// stays self-contained and simple.
const SECTION_META = {
    '/services': { he: 'תוכניות', en: 'Programs', href: '/services' },
    '/blog': { he: 'מדריכים', en: 'Guides', href: '/blog' },
    '/projects': { he: 'העבודות שלנו', en: 'Our Work', href: '/our-work' },
    '/products': { he: 'מוצרים', en: 'Products', href: '/categories' },
    '/events': { he: 'אירועים', en: 'Events', href: '/events' },
};

function formatEventDate(startDate, lang) {
    if (!startDate) return '';
    const d = new Date(startDate);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString(lang === 'he' ? 'he-IL' : 'en-US', { month: 'short', day: 'numeric' });
}

// Build a flat list of simple tiles for the active section.
function buildItems(section, data) {
    const {
        servicesData = [], projectsData = [], blogsData = [],
        productsData = [],
    } = data;

    switch (section) {
        case '/services':
            return servicesData.map((s) => ({
                label: s.card?.title || s.cardTitle || s.name,
                href: `/services/${s.slug}`,
                img: s.card?.image || s.cardImage || s.image || null,
                sub: s.card?.description || s.cardDescription || '',
            }));
        case '/blog':
            return blogsData.map((b) => ({
                label: b.title,
                href: `/blog/${b.slug}`,
                img: b.heroImage || b.image || b.media || null,
                sub: b.excerpt || b.subtitle || '',
            }));
        case '/projects':
            return projectsData.map((p) => ({
                label: p.clientName || p.name,
                href: `/projects/${p.slug}`,
                img: p.mainImage || p.image || null,
                sub: p.subtitle || '',
            }));
        case '/products':
            return productsData.map((p) => ({
                label: p.name || p.title,
                href: `/shop/${p.handle || p.id}`,
                img: p.image || null,
                sub: '',
            }));
        // '/events' is handled separately (Upcoming + Past groups), not here.
        default:
            return [];
    }
}

// Map raw events → mega tiles (date shown in the brand colour).
function eventTiles(list, lang) {
    return list.map((e) => ({
        label: e.title,
        href: `/events/${e.slug}`,
        img: e.image || e.thumbnail || e.seo?.image || null,
        sub: formatEventDate(e.startDate, lang),
        accent: true,
    }));
}

// The reusable image-card grid used for every mega section.
function TileGrid({ tiles, isRtl, go }) {
    return (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-5 md:gap-6">
            {tiles.map((item, i) => (
                <a
                    key={i}
                    href={item.href}
                    onClick={(e) => go(e, item.href)}
                    className={`group block ${isRtl ? 'text-right' : 'text-left'}`}
                >
                    <div className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden bg-primary-50 border border-primary-100">
                            <MediaComponent
                                src={item.img}
                                alt={item.label}
                                quality={40}
                                width={400}
                                useSekeleton={true}
                                wrapperClassName="h-full w-full"
                                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                            />
                       
                    </div>
                    <div className="pt-3.5">
                        <h4 className="text-[17px] font-bold text-primary-500 group-hover:text-secondary-500 transition-colors leading-snug line-clamp-1">
                            {item.label}
                        </h4>
                        {item.sub && (
                            <p className={`text-sm mt-1 leading-snug line-clamp-1 ${item.accent ? 'text-secondary-500 font-bold' : 'text-neutral-500'}`}>
                                {item.sub}
                            </p>
                        )}
                    </div>
                </a>
            ))}
        </div>
    );
}

function MegaNavbarPanel({
    section,
    servicesData = [],
    categoriesData = [], // eslint-disable-line no-unused-vars -- accepted for API compatibility
    productsData = [],
    projectsData = [],
    blogsData = [],
    eventsData = [],
    onNavigate = () => { },
    onMouseLeavePanel = () => { },
    onMouseEnterPanel = () => { },
}) {
    const lang = useSelector((s) => s.systemData?.language || 'he');
    const dir = useSelector((s) => s.systemData?.dir || 'rtl');
    const isRtl = dir === 'rtl';

    const isEvents = section === '/events';

    const items = useMemo(() => {
        if (isEvents) {
            // One row, max 3: upcoming first (soonest), then fill with recent past.
            const upcoming = eventsData.filter((e) => !isPastEvent(e));
            const past = eventsData.filter((e) => isPastEvent(e)).reverse();
            return eventTiles([...upcoming, ...past].slice(0, 3), lang);
        }
        return buildItems(section, { servicesData, projectsData, blogsData, productsData }).slice(0, 3);
    }, [isEvents, section, servicesData, projectsData, blogsData, productsData, eventsData, lang]);

    const meta = SECTION_META[section];
    const title = meta ? meta[lang === 'he' ? 'he' : 'en'] : '';
    const viewAllHref = meta?.href;
    const showMoreLabel = lang === 'he' ? 'הצג עוד' : 'Show more';

    if (!items.length) return null;

    const go = (e, href) => {
        if (e.metaKey || e.ctrlKey) return;
        e.preventDefault();
        onNavigate(href);
    };

    return (
        <div
            role="dialog"
            dir={dir}
            onMouseEnter={onMouseEnterPanel}
            onMouseLeave={onMouseLeavePanel}
            className="w-[min(1080px,100vw)] rounded-[28px] border border-primary-100 bg-white shadow-2xl p-8 md:p-9 origin-top font-primary"
        >
            <motion.div
                key={section}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            >
            {/* Header: section title */}
            <div className="flex items-end justify-between px-1 pb-5 mb-6 border-b border-primary-100">
                <h3 className="text-2xl md:text-[28px] font-bold text-primary-500 leading-none">{title}</h3>
            </div>

            {/* Elegant image-card grid, one row, max 3 (events: upcoming first, then recent past). */}
            <TileGrid tiles={items} isRtl={isRtl} go={go} />

            {/* Show more → full section page */}
            {viewAllHref && (
                <a
                    href={viewAllHref}
                    onClick={(e) => go(e, viewAllHref)}
                    className="group mt-7 pt-5 border-t border-primary-100 flex items-center justify-center gap-1.5 text-[15px] font-bold text-secondary-500 hover:text-secondary-600 transition-colors"
                >
                    <span>{showMoreLabel}</span>
                    <span className={`transition-transform duration-200 ${isRtl ? 'group-hover:-translate-x-0.5' : 'group-hover:translate-x-0.5'}`}>
                        {isRtl ? '←' : '→'}
                    </span>
                </a>
            )}
            </motion.div>
        </div>
    );
}

export default React.memo(MegaNavbarPanel);
